import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { Dialog, DialogHeader } from "@/ds";

import { ConflitoJustificativa } from "../../conflito-justificativa";
import { TagSimulacao } from "../tag-simulacao";
import styles from "./justificativa-conflitos-dialog.module.scss";
import type {
  CartaoPeriodo,
  ConflitoLinha,
  JustificativaConflitosDialogViewModel,
} from "./types-justificativa-conflitos-dialog";

function Cartao({
  rotulo,
  alerta,
  children,
}: {
  rotulo: string;
  alerta?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.card} data-testid="justificativa-conflitos-cartao">
      <span className={styles.cardLabel}>
        <i className={alerta ? "pi pi-calendar-times" : "pi pi-calendar"} aria-hidden="true" />
        {rotulo}
      </span>
      {children}
    </div>
  );
}

function CartaoObra({
  rotulo,
  cartao,
  destaque,
}: {
  rotulo: string;
  cartao: CartaoPeriodo;
  destaque?: boolean;
}) {
  return (
    <Cartao rotulo={rotulo}>
      <span className={styles.cardValue}>{cartao.valor}</span>
      <span
        className={`${styles.cardPeriod} ${destaque ? styles.cardPeriodWarn : ""}`}
        data-testid={destaque ? "justificativa-conflitos-destaque" : undefined}
      >
        {cartao.periodo}
      </span>
    </Cartao>
  );
}

function ConflitoItem({ conflito }: { conflito: ConflitoLinha }) {
  return (
    <div className={styles.conflito} data-testid="justificativa-conflitos-item">
      {conflito.tipo === "ausencia" ? (
        <>
          <p className={styles.text}>
            O profissional <strong>{conflito.recurso}</strong> está com uma ausência programada que
            coincide com o período da obra.
          </p>
          <div className={styles.cards}>
            <Cartao rotulo="Período da obra">
              <span className={styles.cardValue}>{conflito.obra}</span>
            </Cartao>
            <Cartao rotulo="Período Indisponível" alerta>
              <span className={`${styles.cardValue} ${styles.cardValueWarn}`}>
                {conflito.ausencia}
              </span>
            </Cartao>
          </div>
        </>
      ) : (
        <>
          <p className={styles.text}>
            O profissional <strong>{conflito.recurso}</strong> está alocado em outra obra durante o
            período.
          </p>
          <div className={styles.cards}>
            <CartaoObra rotulo="Obra atual" cartao={conflito.atual} destaque />
            <CartaoObra rotulo="Nova alocação" cartao={conflito.nova} />
          </div>
        </>
      )}
      <Message severity="warn" role="note" text={conflito.aviso} />
    </div>
  );
}

export function JustificativaConflitosDialogView({
  formId,
  titulo,
  orientacao,
  voltarLabel,
  confirmarLabel,
  conflitos,
  escopo,
  erro,
  erroRef,
  justificativa,
  confirmar,
  voltar,
}: JustificativaConflitosDialogViewModel) {
  return (
    <Dialog
      visible
      onHide={voltar}
      header={
        <DialogHeader
          icon="exclamation-triangle"
          tone="warn"
          title={
            <>
              {titulo}
              <TagSimulacao quebraNoMobile />
            </>
          }
          subtitle={orientacao}
        />
      }
      footer={
        <div className={styles.footer}>
          <Button
            type="button"
            label={voltarLabel}
            severity="secondary"
            text
            onClick={voltar}
            data-testid="justificativa-conflitos-voltar"
          />
          <Button
            type="submit"
            form={formId}
            label={confirmarLabel}
            data-testid="justificativa-conflitos-confirmar"
          />
        </div>
      }
      data-testid="justificativa-conflitos-dialog"
    >
      <form id={formId} className={styles.body} onSubmit={confirmar} noValidate>
        {conflitos.map((conflito, indice) => (
          <div key={conflito.chave} className={styles.conflito}>
            {indice > 0 ? <hr className={styles.divider} /> : null}
            <ConflitoItem conflito={conflito} />
          </div>
        ))}

        <p className={styles.text}>{escopo}</p>

        <ConflitoJustificativa
          id="justificativa-conflitos"
          value={justificativa.value}
          onChange={justificativa.digitar}
          error={justificativa.error}
          maxLength={justificativa.maxLength}
          testId="justificativa-conflitos-campo"
        />

        {erro ? (
          <div ref={erroRef}>
            <Message severity="error" text={erro} data-testid="justificativa-conflitos-erro" />
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
